import Fuse from 'fuse.js'

// 保存预处理后的toy数据，用于fallback搜索
let processedToysCache = []

/**
 * 清理concept字段的Markdown和HTML内容，提取纯文本用于搜索
 * @param {string} concept - concept.md的原始内容
 * @returns {string} 清理后的纯文本
 */
function cleanConceptForSearch(concept) {
  if (!concept || typeof concept !== 'string') return ''
  
  return concept
    .replace(/```[\s\S]*?```/g, '') // 代码块
    .replace(/`[^`]+`/g, '') // 行内代码
    .replace(/\[([^\]]+)\]\([^\)]+\)/g, '$1') // 链接，保留文本
    .replace(/[#*_~`]/g, '') // Markdown标记符号
    .replace(/\$\$[\s\S]*?\$\$/g, '') // LaTeX块级公式
    .replace(/\$[^$]+\$/g, '') // LaTeX行内公式
    .replace(/<[^>]+>/g, '') // HTML标签
    .replace(/\n+/g, ' ') // 换行符替换为空格
    .trim()
}

/**
 * 预处理toy数据，为搜索准备concept字段
 * @param {Array} toys - 原始toy数据数组
 * @returns {Array} 处理后的toy数据数组
 */
function preprocessToysForSearch(toys) {
  if (!toys || !Array.isArray(toys)) {
    console.warn('preprocessToysForSearch: toys is not an array', toys)
    return []
  }
  
  return toys.map(toy => {
    // 确保toy对象存在且包含必要字段
    if (!toy || typeof toy !== 'object') {
      console.warn('preprocessToysForSearch: invalid toy object', toy)
      return null
    }
    
    // 调试：检查manning-equation的原始数据
    if (toy.id === 'manning-equation') {
      console.log('preprocessToysForSearch: manning-equation原始数据:', {
        id: toy.id,
        title: toy.title,
        titleEn: toy.titleEn,
        keywords: toy.keywords,
        keywordsType: Array.isArray(toy.keywords) ? 'array' : typeof toy.keywords,
        tags: toy.tags,
        description: toy.description
      })
    }
    
    // 将字符串字段转换为小写，确保大小写不敏感搜索
    const lowerCaseString = (str) => typeof str === 'string' ? str.toLowerCase() : str
    
    // 处理keywords数组：转换为小写数组，同时创建一个展平的字符串用于搜索
    let keywordsArray = []
    let keywordsString = ''
    if (Array.isArray(toy.keywords) && toy.keywords.length > 0) {
      keywordsArray = toy.keywords.map(k => lowerCaseString(k))
      keywordsString = keywordsArray.join(' ')
    } else if (toy.keywords) {
      keywordsArray = [lowerCaseString(toy.keywords)]
      keywordsString = lowerCaseString(toy.keywords)
    }
    
    // 处理tags数组：转换为小写数组，同时创建一个展平的字符串用于搜索
    let tagsArray = []
    let tagsString = ''
    if (Array.isArray(toy.tags) && toy.tags.length > 0) {
      tagsArray = toy.tags.map(t => lowerCaseString(t))
      tagsString = tagsArray.join(' ')
    } else if (toy.tags) {
      tagsArray = [lowerCaseString(toy.tags)]
      tagsString = lowerCaseString(toy.tags)
    }
    
    const processed = {
      ...toy,
      // 为concept字段创建清理后的版本用于搜索，并转换为小写
      conceptSearchable: toy.concept ? cleanConceptForSearch(toy.concept).toLowerCase() : '',
      // 保留keywords和tags数组（用于Fuse.js的数组字段搜索）
      keywords: keywordsArray,
      tags: tagsArray,
      // 创建展平的字符串版本用于精确匹配（simpleStringMatch）
      keywordsString: keywordsString,
      tagsString: tagsString,
      // 将title和titleEn转换为小写用于搜索（但保留原始值）
      titleLower: lowerCaseString(toy.title || ''),
      titleEnLower: lowerCaseString(toy.titleEn || ''),
      descriptionLower: lowerCaseString(toy.description || '')
    }
    
    // 调试：检查处理后的数据
    if (toy.id === 'manning-equation') {
      console.log('preprocessToysForSearch: manning-equation处理后数据:', {
        id: processed.id,
        titleLower: processed.titleLower,
        titleEnLower: processed.titleEnLower,
        keywords: processed.keywords,
        keywordsLength: processed.keywords.length
      })
    }
    
    return processed
  }).filter(Boolean) // 过滤掉null值
}

/**
 * Fuse.js 配置选项
 * 配置多字段搜索，标题匹配优先
 * 支持搜索：中文名、英文名、描述、关键词、标签、详情页内容
 */
const fuseOptions = {
  keys: [
    { name: 'titleLower', weight: 0.35 },              // 中文标题权重最高（使用小写版本）
    { name: 'titleEnLower', weight: 0.25 },           // 英文标题次之（使用小写版本）
    { name: 'keywordsString', weight: 0.15 },          // 关键词（展平的字符串，便于部分词匹配如"manning"）
    { name: 'tagsString', weight: 0.12 },              // 标签（展平的字符串，便于部分词匹配）
    { name: 'keywords', weight: 0.10 },          // 关键词数组（作为备用，权重较低）
    { name: 'tags', weight: 0.08 },              // 标签数组（作为备用，权重较低）
    { name: 'descriptionLower', weight: 0.08 },       // 简短描述（使用小写版本）
    { name: 'conceptSearchable', weight: 0.05 }   // 详情页内容（concept.md），权重较低但可搜索
  ],
  threshold: 0.5,           // 匹配阈值（0-1，越小越严格），调整为0.5以支持更好的部分匹配
  distance: 100,            // 匹配距离
  minMatchCharLength: 2,    // 最小匹配字符长度，避免单字母匹配
  includeScore: true,       // 包含匹配分数
  includeMatches: true,     // 包含匹配位置（用于高亮）
  useExtendedSearch: false,
  ignoreLocation: false,     // 不忽略匹配位置，提高匹配精度
  findAllMatches: false,    // 只查找最佳匹配，避免无关结果
  shouldSort: true,         // 按匹配度排序
  sortFn: (a, b) => a.score - b.score  // 按分数升序排序（分数越低越好）
}

/**
 * 创建搜索索引
 * @param {Array} toys - Toy 元数据数组
 * @returns {Fuse} Fuse.js 实例
 */
export function createSearchIndex(toys) {
  if (!toys || !Array.isArray(toys) || toys.length === 0) {
    console.warn('createSearchIndex: invalid toys array', toys)
    processedToysCache = []
    // 返回一个空的Fuse实例
    return new Fuse([], fuseOptions)
  }
  
  // 预处理toy数据，添加conceptSearchable字段
  const processedToys = preprocessToysForSearch(toys)
  
  if (processedToys.length === 0) {
    console.warn('createSearchIndex: no valid toys after preprocessing')
    processedToysCache = []
    return new Fuse([], fuseOptions)
  }
  
  // 保存预处理后的数据用于fallback
  processedToysCache = processedToys
  
  console.log(`创建搜索索引，包含 ${processedToys.length} 个toy`)
  return new Fuse(processedToys, fuseOptions)
}

/**
 * 执行模糊搜索
 * @param {Fuse} searchIndex - Fuse.js 实例
 * @param {string} query - 搜索关键词
 * @returns {Array} 搜索结果数组
 */
/**
 * 简单的字符串包含匹配（用于短查询的fallback）
 * @param {Array} toys - 预处理后的toy数据数组
 * @param {string} query - 搜索查询（小写）
 * @returns {Array} 匹配的结果
 */
function simpleStringMatch(toys, query) {
  if (!query || query.length < 2) return []
  
  const matches = []
  console.log(`simpleStringMatch: 查询 "${query}", 检查 ${toys.length} 个toy`)
  
  for (const toy of toys) {
    let bestScore = 1 // 默认完全不匹配
    let matched = false
    
    // 调试：检查特定toy
    if (toy.id === 'manning-equation' || toy.id === 'linear-regression') {
      console.log(`找到${toy.id} toy:`, {
        id: toy.id,
        titleEnLower: toy.titleEnLower,
        keywords: toy.keywords,
        keywordsString: toy.keywordsString,
        keywordsType: Array.isArray(toy.keywords) ? 'array' : typeof toy.keywords
      })
    }
    
    // 检查各个字段（包括展平的keywords和tags字符串）
    const fields = [
      { value: toy.titleLower, weight: 0.35, name: 'titleLower' },
      { value: toy.titleEnLower, weight: 0.25, name: 'titleEnLower' },
      { value: toy.descriptionLower, weight: 0.08, name: 'descriptionLower' },
      { value: toy.conceptSearchable, weight: 0.05, name: 'conceptSearchable' },
      { value: toy.keywordsString, weight: 0.15, name: 'keywordsString' }, // 使用展平的keywords字符串
      { value: toy.tagsString, weight: 0.12, name: 'tagsString' } // 使用展平的tags字符串
    ]
    
    // 检查字符串字段
    for (const field of fields) {
      if (field.value && typeof field.value === 'string' && field.value.includes(query)) {
        // 计算匹配度：查询在文本中的位置越靠前，匹配度越高
        const index = field.value.indexOf(query)
        const positionRatio = index / Math.max(field.value.length, 1)
        // score越小越好，所以用positionRatio乘以权重
        const fieldScore = positionRatio * field.weight
        bestScore = Math.min(bestScore, fieldScore)
        matched = true
        if (toy.id === 'manning-equation' || toy.id === 'linear-regression') {
          console.log(`匹配到字段 ${field.name}: "${field.value}" 包含 "${query}", score: ${fieldScore}`)
        }
      }
    }
    
    if (matched) {
      matches.push({
        item: toy,
        score: bestScore,
        matches: [] // 简单匹配不提供详细匹配信息
      })
    }
  }
  
  // 按score排序（score越小越好）
  matches.sort((a, b) => a.score - b.score)
  
  console.log(`simpleStringMatch: 找到 ${matches.length} 个匹配结果`)
  
  return matches
}

export function searchToys(searchIndex, query) {
  if (!query || query.trim().length === 0) {
    return []
  }

  if (!searchIndex) {
    console.warn('searchToys: searchIndex is null')
    return []
  }

  // 将查询转换为小写，确保大小写不敏感
  const trimmedQuery = query.trim().toLowerCase()
  console.log(`搜索查询: "${trimmedQuery}"`)
  
  let results = []
  
  // 策略1: 先尝试精确匹配（字符串包含匹配）
  // 这对于"manning"匹配"manning equation"这样的部分词匹配很有效
  if (trimmedQuery.length >= 2) {
    // 获取用于精确匹配的数据源
    let toysToMatch = processedToysCache
    console.log(`processedToysCache长度: ${processedToysCache.length}`)
    
    if (toysToMatch.length === 0) {
      // 如果processedToysCache为空，尝试从searchIndex获取数据
      // Fuse.js内部可能存储了数据
      if (searchIndex._docs && searchIndex._docs.length > 0) {
        toysToMatch = searchIndex._docs
        console.log(`使用searchIndex._docs作为精确匹配数据源，长度: ${toysToMatch.length}`)
      } else {
        console.warn('无法获取数据源进行精确匹配，processedToysCache和searchIndex._docs都为空')
      }
    }
    
    if (toysToMatch.length > 0) {
      const exactMatchResults = simpleStringMatch(toysToMatch, trimmedQuery)
      console.log(`精确匹配找到 ${exactMatchResults.length} 个结果`)
      
      // 调试：显示前3个匹配结果
      if (exactMatchResults.length > 0) {
        console.log('精确匹配结果:', exactMatchResults.slice(0, 3).map(r => ({
          id: r.item?.id,
          title: r.item?.title,
          titleEn: r.item?.titleEn,
          keywords: r.item?.keywords,
          score: r.score
        })))
      }
      
      if (exactMatchResults.length > 0) {
        // 如果精确匹配找到了结果，优先使用精确匹配的结果
        // 但也要检查Fuse.js的结果，看看是否有更好的匹配
        const fuseResults = searchIndex.search(trimmedQuery)
        console.log(`Fuse.js找到 ${fuseResults.length} 个结果`)
        
        // 合并结果，优先使用精确匹配的结果
        // 对于精确匹配的结果，score应该很低（匹配度高）
        const exactMatchIds = new Set(exactMatchResults.map(r => r.item.id))
        
        // 过滤Fuse.js结果：只保留score <= 0.5的结果（匹配度>=50%），避免不相关结果
        // 对于"manning"匹配"manning equation"这样的部分词匹配，score可能在0.4-0.5之间
        const filteredFuseResults = fuseResults.filter(r => r.score <= 0.5)
        
        // 合并结果：精确匹配的结果优先，然后添加Fuse.js的精确匹配结果
        const combinedResults = [...exactMatchResults]
        
        // 添加Fuse.js中不在精确匹配中的结果
        for (const fuseResult of filteredFuseResults) {
          if (!exactMatchIds.has(fuseResult.item.id)) {
            combinedResults.push(fuseResult)
          }
        }
        
        results = combinedResults
      } else {
        // 如果精确匹配没有结果，使用Fuse.js的模糊匹配
        const fuseResults = searchIndex.search(trimmedQuery)
        console.log(`Fuse.js找到 ${fuseResults.length} 个结果`)
        
        // 过滤Fuse.js结果：只保留score <= 0.5的结果（匹配度>=50%），避免不相关结果
        // 对于"manning"匹配"manning equation"这样的部分词匹配，score可能在0.4-0.5之间
        results = fuseResults.filter(r => r.score <= 0.5)
        console.log(`过滤后剩余 ${results.length} 个结果`)
      }
    } else {
      // 如果没有数据源，直接使用Fuse.js
      const fuseResults = searchIndex.search(trimmedQuery)
      console.log(`Fuse.js找到 ${fuseResults.length} 个结果`)
      
      // 过滤Fuse.js结果：只保留score <= 0.5的结果（匹配度>=50%），避免不相关结果
      // 对于"manning"匹配"manning equation"这样的部分词匹配，score可能在0.4-0.5之间
      results = fuseResults.filter(r => r.score <= 0.5)
      console.log(`过滤后剩余 ${results.length} 个结果`)
    }
  }
  
  if (results.length > 0) {
    console.log('前3个结果:', results.slice(0, 3).map(r => ({
      id: r.item?.id,
      title: r.item?.title,
      titleEn: r.item?.titleEn,
      score: r.score,
      matchPercentage: Math.round((1 - r.score) * 100)
    })))
  }
  
  // 转换结果格式，按匹配度排序（已由 Fuse.js 完成）
  // Fuse.js的score范围是0-1，0表示完全匹配，1表示完全不匹配
  // 匹配度百分比 = (1 - score) * 100
  return results.map(result => {
    const matchPercentage = result.score !== undefined 
      ? Math.max(0, Math.min(100, Math.round((1 - result.score) * 100))) 
      : 0
    
    return {
      toy: result.item,
      score: result.score,
      matchPercentage, // 添加匹配度百分比字段
      matches: result.matches || []
    }
  })
}

/**
 * 按分类过滤搜索结果
 * @param {Array} results - 搜索结果
 * @param {string} category - 分类名称
 * @returns {Array} 过滤后的结果
 */
export function filterByCategory(results, category) {
  if (!category) return results
  
  return results.filter(result => 
    result.toy.category === category
  )
}

/**
 * 按难度过滤搜索结果
 * @param {Array} results - 搜索结果
 * @param {string} difficulty - 难度等级
 * @returns {Array} 过滤后的结果
 */
export function filterByDifficulty(results, difficulty) {
  if (!difficulty) return results
  
  return results.filter(result => 
    result.toy.difficulty === difficulty
  )
}

/**
 * 获取搜索建议
 * @param {Fuse} searchIndex - Fuse.js 实例
 * @param {string} query - 搜索关键词
 * @param {number} limit - 建议数量限制
 * @returns {Array} 建议列表
 */
export function getSearchSuggestions(searchIndex, query, limit = 5) {
  if (!query || query.trim().length < 2) {
    return []
  }

  // 确保查询是小写的（searchToys内部会转换，但这里也转换以确保一致性）
  const lowerQuery = typeof query === 'string' ? query.trim().toLowerCase() : query
  const results = searchToys(searchIndex, lowerQuery)
  
  // 返回前 N 个结果的标题作为建议
  return results
    .slice(0, limit)
    .map(result => ({
      id: result.toy.id,
      title: result.toy.title,
      category: result.toy.category
    }))
}
